/**
 * Compliance gate enforcement strip — renders override-required gates
 * above the side panel's ActionFooter with severity coloring and a
 * "Classify with Overrides" CTA.
 */
import { ShieldAlert } from 'lucide-react';
import { FinanceFlagSeverity } from '@signalvote/shared';
import type { RuleOutcomeDto } from '@signalvote/shared';
import { cn } from '@/lib/utils';

interface GateStackProps {
  gateOutcomes: RuleOutcomeDto[];
  overriddenRuleCodes: Set<string>;
  allGatesOverridden: boolean;
  isClassifying: boolean;
  onOverride: (ruleCode: string) => void;
  onClassifyWithOverrides: () => void;
}

export function GateStack({
  gateOutcomes,
  overriddenRuleCodes,
  allGatesOverridden,
  isClassifying,
  onOverride,
  onClassifyWithOverrides,
}: GateStackProps) {
  return (
    <div className="mb-3 space-y-2">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-red-600" />
        <p className="text-sm font-semibold text-red-800">Compliance Gates</p>
      </div>
      {gateOutcomes.map((gate) => {
        const isOverridden = overriddenRuleCodes.has(gate.ruleCode);
        return (
          <div
            key={gate.ruleCode}
            className={cn(
              'rounded-lg border p-3',
              isOverridden
                ? 'border-gray-200 bg-gray-50 opacity-60'
                : gate.severity === FinanceFlagSeverity.Red
                  ? 'border-red-300 bg-red-50'
                  : 'border-amber-300 bg-amber-50',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <p className={cn(
                  'text-sm font-medium',
                  isOverridden ? 'text-gray-500' : gate.severity === FinanceFlagSeverity.Red ? 'text-red-800' : 'text-amber-800',
                )}>
                  {gate.message}
                </p>
                {isOverridden && (
                  <p className="mt-1 text-xs text-gray-500 italic">Overridden</p>
                )}
              </div>
              {gate.overrideAllowed && !isOverridden && (
                <button
                  type="button"
                  onClick={() => onOverride(gate.ruleCode)}
                  className="shrink-0 rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                >
                  Override
                </button>
              )}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={onClassifyWithOverrides}
        disabled={!allGatesOverridden || isClassifying}
        className={cn(
          'w-full rounded-md px-4 py-2 text-sm font-medium transition-colors',
          allGatesOverridden && !isClassifying
            ? 'bg-red-600 text-white hover:bg-red-700'
            : 'bg-red-200 text-red-400 cursor-not-allowed',
        )}
      >
        Classify with Overrides
      </button>
    </div>
  );
}
